import React from 'react';
import { PrintableDocument, PrintStyles } from '../ui/PrintableDocument';
import { formatDate } from '../../lib/utils';
import { Mail, Phone, Building, MapPin, FileText } from 'lucide-react';

interface PrintableClientProps {
  client: any;
  isVisible?: boolean;
}

export const PrintableClient: React.FC<PrintableClientProps> = ({
  client,
  isVisible = false
}) => {
  if (!client) return null;

  return (
    <>
      <PrintStyles />
      <div className={`${isVisible ? 'block' : 'hidden'} print:block`}>
        <PrintableDocument type="client" data={client}>
          {/* Client Header */}
          <div className="text-center mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building className="w-12 h-12 text-primary-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{client.name}</h2>
            {client.company && (
              <p className="text-xl text-gray-600">{client.company}</p>
            )}
          </div>

          {/* Client Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                CONTACT INFORMATION
              </h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{client.email}</p>
                  </div>
                </div>
                
                {client.phone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Phone</p>
                      <p className="font-medium text-gray-900">{client.phone}</p>
                    </div>
                  </div>
                )}

                {client.company && (
                  <div className="flex items-center space-x-3">
                    <Building className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-medium text-gray-900">{client.company}</p>
                    </div>
                  </div>
                )}

                {client.address && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-gray-500 mt-1" />
                    <div>
                      <p className="text-sm text-gray-600">Address</p>
                      <p className="font-medium text-gray-900 whitespace-pre-line">{client.address}</p>
                    </div>
                  </div>
                )}

                {client.gstin && (
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">GSTIN</p>
                      <p className="font-medium text-gray-900">{client.gstin}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Account Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-200 pb-2">
                ACCOUNT INFORMATION
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Client Since:</span>
                  <span className="font-medium">{formatDate(client.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="font-medium">{formatDate(client.updated_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Client ID:</span>
                  <span className="font-medium font-mono">#{client.id.slice(-8)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Client Summary Table */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">CLIENT SUMMARY</h3>
            <table className="document-table w-full">
              <tbody>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-700">Full Name:</td>
                  <td className="py-3 px-4 font-semibold">{client.name}</td>
                </tr>
                {client.company && (
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-700">Company:</td>
                    <td className="py-3 px-4">{client.company}</td>
                  </tr>
                )}
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-700">Email:</td>
                  <td className="py-3 px-4">{client.email}</td>
                </tr>
                {client.phone && (
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-700">Phone:</td>
                    <td className="py-3 px-4">{client.phone}</td>
                  </tr>
                )}
                {client.address && (
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-700">Address:</td>
                    <td className="py-3 px-4 whitespace-pre-line">{client.address}</td>
                  </tr>
                )}
                {client.gstin && (
                  <tr>
                    <td className="py-3 px-4 font-medium text-gray-700">GSTIN:</td>
                    <td className="py-3 px-4 font-mono">{client.gstin}</td>
                  </tr>
                )}
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-700">Client Since:</td>
                  <td className="py-3 px-4">{formatDate(client.created_at)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Client Information Footer */}
          <div className="mt-8 p-4 bg-primary-50 border border-primary-200 rounded-lg">
            <h4 className="font-semibold text-primary-900 mb-2">Client Information</h4>
            <p className="text-primary-800 text-sm">
              This document contains confidential client information. 
              Please handle with care and maintain appropriate privacy standards.
            </p>
          </div>
        </PrintableDocument>
      </div>
    </>
  );
};